import React from "react";

import { Box } from "@mui/material";

import { Destination } from "Constant/destination";

import BigqueryDestination from "components/destination_types/BigqueryDestination";
import LinkedinSignalDestination from "components/organisms/CRMs/LinkedinSignalDestination";
import MetaCustomAudience from "components/organisms/CRMs/MetaCustomAudience";
import MicrosoftBingDestination from "components/organisms/CRMs/MicrosoftBingDestination";
import TiktokCustomAudience from "components/organisms/CRMs/TiktokCustomAudience";
import GoogleAdsConversion from "components/organisms/Destinations/GoogleAdsConversion";
import GoogleAnalytics from "components/organisms/Destinations/GoogleAnalytics";
import Impact from "components/organisms/Destinations/Impact";
import OpenAiAdsCreateFields from "components/organisms/Destinations/OpenAiAds/OpenAiAdsCreateFields";
import SignalSightTaskDestination from "components/organisms/Destinations/SignalSightTaskDestination";
import YandexMetricaDestination from "components/organisms/Destinations/YandexMetrica";
import PinterestAuthAndSelectPixels from "components/PinterestAuthAndSelectPixels";

import AccessTokenField from "./components/AccessTokenField";
import CreatePixelToggle, {
    CreatePixelTrackingHelp,
} from "./components/CreatePixelToggle";
import DestinationMappingView from "./components/DestinationMappingView";
import FacebookCapiForm from "./components/FacebookCapiForm";
import GoogleEnhancedForm from "./components/GoogleEnhancedForm";
import PinterestCapiFields from "./components/PinterestCapiFields";
import PixelIdField from "./components/PixelIdField";
import RedditCapiForm from "./components/RedditCapiForm";
import TiktokAppIdField from "./components/TiktokAppIdField";
import TiktokDestinationForm from "./components/TiktokDestinationForm";
import TwitterDestinationForm from "./components/TwitterDestinationForm";
import {
    CREATE_PIXEL_TOGGLE_TYPES,
    GOOGLE_ENHANCED_TYPES,
    PINTEREST_TYPES,
    PIXEL_FIELD_EXCLUDED_TYPES,
    TIKTOK_TYPES,
    TOKEN_FIELD_EXCLUDED_TYPES,
    TWITTER_TYPES,
} from "./DestinationCreation.constants";

const preventSubmit = (event) => event.preventDefault();

/**
 * Renders the destination creation form (or the post-create mapping view) for
 * the state returned by `useDestinationCreation`. The destination type is
 * picked in the catalog step beforehand.
 */
const DestinationCreationForm = ({ destinationCreation }) => {
    const {
        language,
        appObject,
        isTrackerLoading,
        hostActions,
        form,
        pinterest,
        facebook,
        reddit,
        tiktok,
        twitter,
        customAudience,
        googleEnhanced,
        linkedin,
        microsoftBing,
        yandexMetrica,
        googleAds,
        mappingOnly,
        oauthData,
        setOauthData,
        isAnyMappingOpen,
        setSaveDisabled,
        setIsSaveAvailable,
        isButtonLoader,
        setIsButtonLoader,
        setModalLoading,
        submitButtonRef,
        registerFooterAction,
        completeCreation,
    } = destinationCreation;

    if (isAnyMappingOpen) {
        return (
            <DestinationMappingView destinationCreation={destinationCreation} />
        );
    }

    const { destinationCode, credentials } = form;
    const appId = appObject.app_id && appObject.app_id.toString();
    const appVersion = appObject.version && appObject.version.toString();
    const isRedditConnected =
        destinationCode === Destination.rdcapi && !!reddit.advertisers;

    const isPixelFieldVisible =
        !PIXEL_FIELD_EXCLUDED_TYPES.includes(destinationCode) &&
        !googleAds.isConversionType &&
        !tiktok.hasAccess &&
        pinterest.isAuthButtonVisible;
    const isPixelInputVisible =
        !!destinationCode &&
        destinationCode !== Destination.pncapi &&
        !isRedditConnected;
    const isTokenFieldVisible =
        !!destinationCode &&
        !TOKEN_FIELD_EXCLUDED_TYPES.includes(destinationCode) &&
        !googleAds.isConversionType &&
        !tiktok.hasAccess &&
        !isRedditConnected;

    const renderProviderLogin = () => {
        if (TIKTOK_TYPES.includes(destinationCode)) {
            return (
                <TiktokDestinationForm
                    language={language}
                    destinationCode={destinationCode}
                    tiktok={tiktok}
                    oauthData={oauthData}
                    setOauthData={setOauthData}
                />
            );
        }
        if (destinationCode === Destination.fbcapi) {
            return (
                <FacebookCapiForm
                    language={language}
                    appObject={appObject}
                    facebook={facebook}
                    isLoading={
                        isButtonLoader || facebook.isLoading || isTrackerLoading
                    }
                    setIsButtonLoader={setIsButtonLoader}
                />
            );
        }
        if (PINTEREST_TYPES.includes(destinationCode)) {
            return (
                <div className="mb-3">
                    <PinterestAuthAndSelectPixels
                        createPixelStatus={form.createPixelStatus}
                        destinationType={destinationCode}
                        setPinterestFormStatus={pinterest.setFormStatus}
                        setIsPinterestAuthButtonVisible={
                            pinterest.setIsAuthButtonVisible
                        }
                        setPinterestCapiData={pinterest.setCapiData}
                        setIsSaveReady={setIsSaveAvailable}
                        setIsAuthorised={pinterest.setIsAuthorised}
                    />
                </div>
            );
        }
        return null;
    };

    return (
        <Box component="form" noValidate onSubmit={preventSubmit}>
            <div>
                {destinationCode === Destination.rdcapi && (
                    <RedditCapiForm reddit={reddit} language={language} />
                )}

                <GoogleAnalytics
                    destinationCode={destinationCode}
                    value={form.googleTagId}
                    setValue={form.setGoogleTagId}
                    setButtonDisabledStatus={setSaveDisabled}
                />
                <Impact
                    destinationCode={destinationCode}
                    setButtonDisabledStatus={setSaveDisabled}
                    onDestinationIDChange={mappingOnly.setImpactDestinationId}
                    onFieldsChange={mappingOnly.setImpactFields}
                />

                {renderProviderLogin()}

                {destinationCode === Destination.fbca && (
                    <MetaCustomAudience
                        isLoadingButton={isButtonLoader}
                        fbExtras={facebook.beExtra}
                        appId={appId}
                        version={appVersion}
                        fbExternalBusinessID={facebook.externalBusinessId}
                        setMetaCustomAudienceInfos={customAudience.setMetaInfos}
                        setIsSaveAvailable={setIsSaveAvailable}
                    />
                )}

                {destinationCode === Destination.ttca && (
                    <TiktokCustomAudience
                        setTiktokCustomAudienceInfos={
                            customAudience.setTiktokInfos
                        }
                        setIsSaveAvailable={setIsSaveAvailable}
                    />
                )}

                {TWITTER_TYPES.includes(destinationCode) && (
                    <TwitterDestinationForm
                        destinationCode={destinationCode}
                        twitter={twitter}
                        oauthData={oauthData}
                        setOauthData={setOauthData}
                        setIsButtonLoader={setIsButtonLoader}
                    />
                )}

                {destinationCode === Destination.ggbq && (
                    <BigqueryDestination
                        registerFooterAction={registerFooterAction}
                        getAllDestinationsRedux={
                            hostActions.refreshDestinations
                        }
                        setIsLoading={setModalLoading}
                        language={language}
                        tracker_id={hostActions.trackerId}
                        getTracker={hostActions.getTracker}
                        handleCloseModal={hostActions.close}
                        onDestinationCreated={completeCreation}
                    />
                )}

                {destinationCode === Destination.licapi && (
                    <LinkedinSignalDestination
                        setLinkedinSignalData={linkedin.setData}
                        setLinkedinAuthenticated={linkedin.setIsAuthenticated}
                    />
                )}

                {destinationCode === Destination.msoffline && (
                    <MicrosoftBingDestination
                        setMicrosoftBingData={microsoftBing.setData}
                        setMicrosoftBingAuthenticated={
                            microsoftBing.setIsAuthenticated
                        }
                    />
                )}

                {destinationCode === Destination.ymwc && (
                    <YandexMetricaDestination
                        setYandexMetricaData={yandexMetrica.setData}
                        setYandexMetricaAuthenticated={
                            yandexMetrica.setIsAuthenticated
                        }
                    />
                )}

                {googleAds.isConversionType && (
                    <GoogleAdsConversion
                        type={destinationCode}
                        setGoogleAdsData={googleAds.setData}
                        setGoogleAdsAuthenticated={googleAds.setIsAuthenticated}
                    />
                )}

                {isPixelFieldVisible && (
                    <PixelIdField
                        destinationCode={destinationCode}
                        value={credentials?.pixel_id}
                        error={form.pixelIdError}
                        isInputVisible={isPixelInputVisible}
                        language={language}
                        onChange={form.handlePixelChange}
                    />
                )}

                {destinationCode === Destination.sstask && (
                    <SignalSightTaskDestination
                        destinationCode={destinationCode}
                        setButtonDisabledStatus={setSaveDisabled}
                        onFieldsChange={mappingOnly.setSignalSightTaskFields}
                        onNameChange={mappingOnly.setSignalSightTaskName}
                        buttonRef={submitButtonRef}
                    />
                )}

                {destinationCode === Destination.ttapp && (
                    <TiktokAppIdField
                        value={credentials?.app_id}
                        error={form.appIdError}
                        language={language}
                        onChange={form.handleAppIdChange}
                    />
                )}

                {destinationCode === Destination.oaicapi && (
                    <OpenAiAdsCreateFields
                        conversionsApiKey={credentials?.conversions_api_key}
                        advertiserApiKey={credentials?.advertiser_api_key}
                        onConversionsApiKeyChange={form.handleOpenAiKeyChange(
                            "conversions_api_key",
                            form.setConversionsKeyError,
                        )}
                        onAdvertiserApiKeyChange={form.handleOpenAiKeyChange(
                            "advertiser_api_key",
                            form.setAdvertiserKeyError,
                        )}
                        conversionsApiKeyError={form.conversionsKeyError}
                        advertiserApiKeyError={form.advertiserKeyError}
                        createPixelStatus={form.createPixelStatus}
                        onCreatePixelStatusChange={form.handlePixelStatusChange}
                        createPixelQuestion={
                            language?.buttons?.create_pixel_tracking_question
                        }
                        createPixelPopover={
                            <CreatePixelTrackingHelp language={language} />
                        }
                    />
                )}

                {isTokenFieldVisible && (
                    <AccessTokenField
                        destinationCode={destinationCode}
                        value={credentials?.token}
                        error={form.accessTokenError}
                        language={language}
                        onChange={form.handleTokenChange}
                    />
                )}

                {GOOGLE_ENHANCED_TYPES.includes(destinationCode) && (
                    <GoogleEnhancedForm
                        language={language}
                        destinationCode={destinationCode}
                        googleEnhanced={googleEnhanced}
                        setSaveDisabled={setSaveDisabled}
                    />
                )}

                {destinationCode === Destination.pncapi &&
                    pinterest.isAuthButtonVisible && (
                        <PinterestCapiFields
                            language={language}
                            formData={pinterest.formData}
                            onChange={pinterest.handleInputChange}
                            accountIdError={form.pixelIdError}
                            accessTokenError={form.accessTokenError}
                        />
                    )}
            </div>

            {CREATE_PIXEL_TOGGLE_TYPES.includes(destinationCode) && (
                <CreatePixelToggle
                    checked={form.createPixelStatus}
                    onChange={form.handlePixelStatusChange}
                    language={language}
                />
            )}
        </Box>
    );
};

export default DestinationCreationForm;
